<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KnowledgeAPI from 'dashboard/api/knowledge';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import KnowledgeField from './components/KnowledgeField.vue';
import { PROFILES } from './config';

const props = defineProps({
  section: { type: String, required: true },
});

const { t } = useI18n();
const config = computed(() => PROFILES[props.section]);
const i18nPrefix = computed(() => `KNOWLEDGE.${config.value.i18nKey}`);
const fields = computed(() => config.value.sections.flatMap(section => section.fields));

const form = ref({});
const isLoading = ref(true);
const isSaving = ref(false);

const toForm = profile =>
  Object.fromEntries(
    fields.value.map(({ name, kind }) => {
      if (kind === 'switch') return [name, Boolean(profile[name])];
      return [name, profile[name] ?? ''];
    })
  );

const toPayload = () =>
  Object.fromEntries(
    Object.entries(form.value).map(([name, value]) => [
      name,
      value === '' ? null : value,
    ])
  );

const fetchProfile = async () => {
  isLoading.value = true;
  try {
    const { data } = await KnowledgeAPI.getProfile(config.value.type);
    form.value = toForm(data);
  } catch (error) {
    useAlert(t('KNOWLEDGE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await KnowledgeAPI.updateProfile(config.value.type, toPayload());
    form.value = toForm(data);
    useAlert(t('KNOWLEDGE.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KNOWLEDGE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchProfile);
</script>

<template>
  <SettingsLayout :is-loading="isLoading" :loading-message="t('KNOWLEDGE.LOADING')">
    <template #header>
      <BaseSettingsHeader
        :title="t(`${i18nPrefix}.TITLE`)"
        :description="t(`${i18nPrefix}.DESCRIPTION`)"
      />
    </template>
    <template #body>
      <form class="flex flex-col gap-4 max-w-3xl" @submit.prevent="save">
        <section
          v-for="section in config.sections"
          :key="section.key"
          class="flex flex-col gap-4 p-5 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-col gap-1">
            <h3 class="text-base font-medium text-n-slate-12">
              {{ t(`${i18nPrefix}.SECTIONS.${section.key}.TITLE`) }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t(`${i18nPrefix}.SECTIONS.${section.key}.DESCRIPTION`) }}
            </p>
          </div>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <KnowledgeField
              v-for="field in section.fields"
              :key="field.name"
              v-model="form[field.name]"
              :field="field"
              :i18n-prefix="i18nPrefix"
              :class="{ 'md:col-span-2': field.kind !== 'input' }"
            />
          </div>
        </section>
        <div
          class="sticky bottom-0 z-10 flex justify-end py-3 border-t border-n-weak bg-n-surface-1"
        >
          <Button
            type="submit"
            :label="t('KNOWLEDGE.SAVE')"
            :is-loading="isSaving"
          />
        </div>
      </form>
    </template>
  </SettingsLayout>
</template>
